// Sopwith 3D - heads-up display.
//
// DOM overlay: gauges for lives, fuel, bombs and ammo at the top, and a
// controls legend at the bottom that can be hidden with ? or by clicking it.

import { MAXBOMBS, MAXFUEL, MAXROUNDS } from '../sim/constants';
import type { Game } from '../sim/game';
import { MEDAL_NAMES, RIBBON_NAMES } from '../sim/medals';
import { ObjState } from '../sim/types';
import { Minimap } from './minimap';

const CONTROLS: [string, string][] = [
  ['X / Z', 'throttle up / down'],
  [', or ↑', 'pull up'],
  ['/ or ↓', 'pull down'],
  ['.', 'roll'],
  ['Space', 'machine gun'],
  ['B', 'drop bomb'],
  ['H', 'autopilot home'],
  ['S', 'sound on/off'],
  ['R', 'retro mode'],
  ['?', 'hide this'],
];

export class Hud {
  private readonly root: HTMLElement;
  private readonly lives: HTMLElement;
  private readonly fuel: HTMLElement;
  private readonly bombs: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly score: HTMLElement;
  private readonly message: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly flash: HTMLElement;
  private readonly awards: HTMLElement;
  private readonly gauges: HTMLElement;
  readonly minimap: Minimap;
  private retro = false;
  private lastKey = '';
  private splatUntil = 0;
  private splatText = '';
  private awardsKey = '';

  constructor(container: HTMLElement) {
    this.root = el('div', 'hud');
    container.appendChild(this.root);

    const gauges = el('div', 'hud-gauges');
    this.gauges = gauges;
    this.root.appendChild(gauges);
    this.minimap = new Minimap(this.root);
    this.lives = this.gauge(gauges, 'planes');
    this.fuel = this.gauge(gauges, 'fuel', true);
    this.bombs = this.gauge(gauges, 'bombs');
    this.ammo = this.gauge(gauges, 'ammo', true);
    this.score = el('div', 'hud-score');
    gauges.appendChild(this.score);
    this.awards = el('div', 'hud-awards');
    this.root.appendChild(this.awards);

    this.message = el('div', 'hud-message hidden');
    this.root.appendChild(this.message);
    this.flash = el('div', 'hud-flash');
    this.root.appendChild(this.flash);

    this.legend = el('div', 'hud-legend');
    for (const [key, what] of CONTROLS) {
      const row = el('div', 'hud-row');
      const k = el('kbd');
      k.textContent = key;
      const w = el('span');
      w.textContent = what;
      row.append(k, w);
      this.legend.appendChild(row);
    }
    this.legend.addEventListener('click', () => this.toggleLegend());
    this.root.appendChild(this.legend);
    window.addEventListener('keydown', (e) => {
      if (e.key === '?' || e.code === 'Slash' && e.shiftKey) {
        this.toggleLegend();
      }
    });
  }

  toggleLegend(): void {
    this.legend.classList.toggle('hidden');
  }

  setVisible(v: boolean): void {
    this.root.hidden = !v;
  }

  /** Retro mode: the classic status bar carries the gauges and map itself. */
  setRetro(retro: boolean): void {
    this.retro = retro;
    this.gauges.hidden = retro;
    this.awards.hidden = retro;
    this.minimap.setVisible(!retro);
  }

  /** Redraw the minimap with the camera's visible span. */
  drawMinimap(g: Game, viewLeft: number, viewRight: number): void {
    if (!this.retro) this.minimap.draw(g, viewLeft, viewRight);
  }

  /** Show a message for a while (level cleared, sound toggled). */
  flashMessage(text: string, ms = 1500): void {
    this.splatText = text;
    this.splatUntil = performance.now() + ms;
  }

  /** A bullet through the windscreen flashes the screen; animals splat. */
  onCollision(kind: 'windshot' | 'splatox' | 'splatbird'): void {
    if (kind === 'windshot') {
      this.flash.classList.remove('hit');
      void this.flash.offsetWidth; // restart the animation
      this.flash.classList.add('hit');
    } else {
      this.splatText = kind === 'splatox' ? 'SPLAT! (ox: -200)' : 'SPLAT! (bird: -25)';
      this.splatUntil = performance.now() + 1500;
    }
  }

  private gauge(parent: HTMLElement, label: string, bar = false): HTMLElement {
    const box = el('div', 'hud-gauge');
    const l = el('span', 'hud-label');
    l.textContent = label;
    const v = el('div', bar ? 'hud-bar' : 'hud-pips');
    if (bar) {
      v.appendChild(el('div', 'hud-fill'));
    }
    box.append(l, v);
    parent.appendChild(box);
    return v;
  }

  private pips(target: HTMLElement, count: number, max: number): void {
    const key = `${count}/${max}`;
    if (target.dataset.key === key) return;
    target.dataset.key = key;
    target.replaceChildren();
    for (let i = 0; i < max; i++) {
      const p = el('span', i < count ? 'pip on' : 'pip');
      target.appendChild(p);
    }
  }

  private bar(target: HTMLElement, value: number, max: number): void {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    (target.firstElementChild as HTMLElement).style.width = `${pct.toFixed(1)}%`;
    target.classList.toggle('low', pct < 20);
  }

  update(g: Game): void {
    const p = g.consoleplayer;
    this.pips(this.lives, Math.max(0, g.maxcrash - p.crashcnt), g.maxcrash);
    this.bar(this.fuel, p.life, MAXFUEL);
    this.pips(this.bombs, p.bombs, MAXBOMBS);
    this.bar(this.ammo, p.rounds, MAXROUNDS);
    this.score.textContent = String(p.score.score);

    const key = `${p.score.medals.join(',')}|${p.score.ribbons.join(',')}`;
    if (key !== this.awardsKey) {
      this.awardsKey = key;
      this.awards.replaceChildren();
      for (const m of p.score.medals) {
        const a = el('span', 'hud-award');
        a.textContent = '🎖';
        a.title = MEDAL_NAMES[m];
        this.awards.appendChild(a);
      }
      for (const r of p.score.ribbons) {
        const a = el('span', 'hud-award ribbon');
        a.textContent = '🎗';
        a.title = RIBBON_NAMES[r];
        this.awards.appendChild(a);
      }
    }

    let msg = '';
    if (performance.now() < this.splatUntil) {
      msg = this.splatText;
    } else if (g.isOver) {
      msg = 'GAME OVER';
    } else if (p.state === ObjState.CRASHED) {
      msg = 'CRASHED';
    } else if (p.state === ObjState.STALLED || p.state === ObjState.WOUNDSTALL) {
      msg = 'STALL  hold X and ↓ until the nose is down and speed returns, then pull up';
    } else if (p.athome && p.state === ObjState.FLYING && g.countmove > 0 && !g.successfulFlight) {
      msg = 'Hold X to take off, then pull up with ,';
    }
    if (msg !== this.lastKey) {
      this.lastKey = msg;
      this.message.textContent = msg;
      this.message.classList.toggle('hidden', msg === '');
    }
  }
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}
